import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import styled from 'styled-components';

import {
  METER, TRADE,
} from 'src/enosikit/components/Chart/components/chartConstants';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PORTFOLIO,
  DATA_AGGREGATE_BY_PROPERTY, DATA_GROUP_BY_COUNTERPARTY,
  DATA_GROUP_BY_TRADE_TYPE,
} from 'src/util/constants';

import { getTooltipPosition, getTooltipWidth } from '../helpers/tooltip';
// Styles
const base = 2.5;

const TooltipContainer = styled.div`
width: max-content;
fontSize: ${base * 0.25}rem;
fontFamily: inherit;
color: #212529;
padding: ${base * 0.3}rem;
background-color: #fff;
border: ${base * 0.1}rem solid #f5f7fa;
border-radius: ${base * 0.1}rem;
`;

const MainHeader = styled.h5`
font-size: ${base * 0.4}rem;
margin-bottom: ${base * 0.1}rem;
`;

// End of child components rendering

/**
 * Description
 * @param {any} props
 * @returns {React.ReactElement} - ChartTooltip component
 */
function ChartTooltip({
  chartType, chartWidth,
  chartView, datum = undefined, tooltipUpdate,
  tooltipProps = {
    tooltipFormat: null,
    tooltipTimestamp: undefined,
    unit: undefined,
  }, x = undefined,
}) {
  const {
    unit,
    unitLabel,
    tooltipDateFormat,
    tooltipData,
    tooltipLabelFunc,
    tooltipTimestamp,
  } = tooltipProps;

  const tooltipRef = useRef(null);

  if (!datum) {
    return null;
  }

  const { fullDateObj: datumTimestamp } = datum;

  if (!tooltipTimestamp && !datumTimestamp) {
    return null;
  }

  // This happens for property with 0 volume usage.
  if (!tooltipTimestamp) {
    tooltipUpdate(datumTimestamp);
  }
  const { ts: tooltipTimestampValue } = tooltipTimestamp || datumTimestamp; // Is required property.

  if (datumTimestamp) {
    const { ts: datumTimestampValue } = datumTimestamp;

    // This happens when the tooltip is trigerred from the whitespace above the bars.
    // We need to update the parent component with the correct timestamp to get the tooltip data
    if (datumTimestampValue && datumTimestampValue !== tooltipTimestampValue) {
      tooltipUpdate(datumTimestamp);
    }
  }

  const tooltipWidth = getTooltipWidth(tooltipRef);

  return (
    <g>
      <foreignObject x={getTooltipPosition(x, chartWidth, tooltipWidth)} width="1" height="100%" className="overflow-visible">
        <TooltipContainer ref={tooltipRef} className={`${chartType}-tooltip`}>
          <MainHeader>{tooltipTimestamp?.toLocaleString(tooltipDateFormat)}</MainHeader>
          {
            tooltipLabelFunc(
              tooltipData,
              unitLabel,
              chartView,
              unit,
            )
          }
        </TooltipContainer>
      </foreignObject>
    </g>
  );
}

export default ChartTooltip;

ChartTooltip.propTypes = {
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
  }).isRequired,
  chartType: PropTypes.oneOf([METER, TRADE]).isRequired,
  chartWidth: PropTypes.number.isRequired,
  datum: PropTypes.oneOfType([PropTypes.object]),
  tooltipProps: PropTypes.shape({
    isCarbon: PropTypes.bool.isRequired,
    unit: PropTypes.string,
    unitLabel: PropTypes.string.isRequired,
    tooltipDateFormat: PropTypes.oneOfType([PropTypes.object]).isRequired,
    tooltipData: PropTypes.oneOfType([PropTypes.object]).isRequired,
    tooltipFormat: PropTypes.string,
    tooltipLabelFunc: PropTypes.func.isRequired,
    tooltipTimestamp: PropTypes.instanceOf(DateTime),
  }),
  tooltipUpdate: PropTypes.func.isRequired,
  x: PropTypes.number,
};
